/**
 * Linhas da tela Agenda (plano, 11.1 função 1): atendimentos, bloqueios e
 * horários livres como espaços tocáveis, dentro das faixas abertas do dia.
 * Fica no core para o app nativo (fase 4) montar a mesma lista.
 */
import type { Appointment, Block } from '../schemas/entities';
import { occupiesTime } from './appointments';
import { type LocalDate, MINUTE, overlaps, todayIn, toIso, toMs, utcToZoned, zonedToUtc } from './time';

export type AgendaRow<T extends { appointment: Appointment }> =
  | { kind: 'free'; start: string; end: string; time: string }
  | { kind: 'appointment'; start: string; item: T }
  | { kind: 'block'; start: string; block: Block }
  | { kind: 'now'; start: string };

export function buildAgendaRows<T extends { appointment: Appointment }>(params: {
  date: LocalDate;
  timezone: string;
  items: T[];
  blocks: Block[];
  openRanges: Array<{ start: string; end: string }>;
  now: number;
  stepMin?: number;
}): AgendaRow<T>[] {
  const { date, timezone: tz, now } = params;
  const step = (params.stepMin ?? 30) * MINUTE;
  const busy = [
    ...params.items.filter((i) => occupiesTime(i.appointment)).map((i) => [toMs(i.appointment.start_utc), toMs(i.appointment.end_utc)] as const),
    ...params.blocks.map((b) => [toMs(b.start_utc), toMs(b.end_utc)] as const),
  ];
  const isToday = todayIn(tz, now) === date;
  const nowFloor = Math.floor(now / step) * step;

  const rows: AgendaRow<T>[] = [];
  for (const range of params.openRanges) {
    const rs = zonedToUtc(date, range.start, tz);
    const re = zonedToUtc(date, range.end, tz);
    const local = utcToZoned(rs, tz);
    const rem = (local.minutes * MINUTE) % step;
    for (let t = rem === 0 ? rs : rs + (step - rem); t + step <= re; t += step) {
      if (isToday && t < nowFloor) continue;
      if (busy.some(([s, e]) => overlaps(t, t + step, s, e))) continue;
      rows.push({ kind: 'free', start: toIso(t), end: toIso(t + step), time: utcToZoned(t, tz).time });
    }
  }
  for (const item of params.items) rows.push({ kind: 'appointment', start: item.appointment.start_utc, item });
  for (const block of params.blocks) rows.push({ kind: 'block', start: block.start_utc, block });

  const order = (r: AgendaRow<T>) => (r.kind === 'now' ? 1 : r.kind === 'free' ? 2 : 0);
  rows.sort((a, b) => toMs(a.start) - toMs(b.start) || order(a) - order(b));

  if (isToday && rows.length > 0) {
    const idx = rows.findIndex((r) => toMs(r.start) >= now || (r.kind === 'appointment' && toMs(r.item.appointment.end_utc) > now));
    const at = idx === -1 ? rows.length : idx;
    if (at > 0) rows.splice(at, 0, { kind: 'now', start: toIso(now) });
  }
  return rows;
}

/** Linha de horário livre já juntada com as vizinhas: "12h, 2 horas livres". */
export type FreeGap = { kind: 'free'; start: string; end: string; time: string; minutes: number };

export type AgendaListRow<T extends { appointment: Appointment }> = Exclude<AgendaRow<T>, { kind: 'free' }> | FreeGap;

/**
 * Junta os livres seguidos numa linha só (docs/design: "2 horas livres" com "Marcar").
 * A linha "agora" que cair no meio de um livre vai para antes dele.
 */
export function mergeFreeRows<T extends { appointment: Appointment }>(rows: AgendaRow<T>[]): AgendaListRow<T>[] {
  const out: AgendaListRow<T>[] = [];
  let gap: FreeGap | null = null;
  let pendingNow: AgendaRow<T> | null = null;
  const flush = () => {
    if (gap) out.push(gap);
    gap = null;
  };
  for (const r of rows) {
    if (r.kind === 'free') {
      if (gap && gap.end === r.start) {
        gap.end = r.end;
        gap.minutes = Math.round((toMs(r.end) - toMs(gap.start)) / MINUTE);
      } else {
        flush();
        if (pendingNow) out.push(pendingNow);
        pendingNow = null;
        gap = { kind: 'free', start: r.start, end: r.end, time: r.time, minutes: Math.round((toMs(r.end) - toMs(r.start)) / MINUTE) };
      }
      continue;
    }
    if (r.kind === 'now' && gap) {
      // "agora" dentro de um livre: fica antes do livre, que continua juntando
      out.push(r);
      continue;
    }
    flush();
    if (pendingNow) out.push(pendingNow);
    pendingNow = null;
    out.push(r);
  }
  flush();
  if (pendingNow) out.push(pendingNow);
  return out;
}

/** "2 horas livres", "1h30 livres", "20 min livres". */
export function freeTimeLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} min livres`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (m === 0) return h === 1 ? '1 hora livre' : `${h} horas livres`;
  return `${h}h${String(m).padStart(2, '0')} livres`;
}

/** Serviços que cabem num livre, do mais longo para o mais curto (para "Cabe pé e mão ou limpeza de pele"). */
export function servicesThatFit<S extends { name: string; duration_min: number; active?: boolean }>(services: S[], minutes: number, max = 2): S[] {
  return services
    .filter((s) => s.active !== false && s.duration_min <= minutes)
    .sort((a, b) => b.duration_min - a.duration_min)
    .slice(0, max);
}

/** Quem está sendo atendida agora e quem vem depois (painel "Agora" da Agenda). */
export function nowAndNext<T extends { appointment: Appointment }>(items: T[], now: number): { current: T | null; next: T | null } {
  const waiting = (a: Appointment) => a.status === 'scheduled' || a.status === 'confirmed';
  const sorted = [...items].sort((a, b) => toMs(a.appointment.start_utc) - toMs(b.appointment.start_utc));
  const current = sorted.find((i) => waiting(i.appointment) && toMs(i.appointment.start_utc) <= now && now < toMs(i.appointment.end_utc)) ?? null;
  const next = sorted.find((i) => i !== current && waiting(i.appointment) && toMs(i.appointment.start_utc) > now) ?? null;
  return { current, next };
}

/** Resumo do dia: "4 atendimentos, R$ 405 previstos" (pedidos ainda não aceitos ficam de fora). */
export function daySummary(items: Array<{ appointment: Appointment }>): { count: number; expectedCents: number; requests: number } {
  const counted = items.filter((i) => occupiesTime(i.appointment) && i.appointment.status !== 'pending');
  return {
    count: counted.length,
    expectedCents: counted.reduce((sum, i) => sum + Math.max(0, i.appointment.price_cents - (i.appointment.discount_cents ?? 0)), 0),
    requests: items.filter((i) => i.appointment.status === 'pending').length,
  };
}
